import React from 'react';
import { Trans } from 'react-i18next';

// Shared styling for every link embedded in disclaimer/consent copy via
// <Trans components={{ ... }}>, across DisclaimerContent and the inline
// disclaimers in SSNVerificationForm and ContactInfoForm.
//
// Tailwind's Preflight base styles reset anchors to `color: inherit;
// text-decoration: inherit`, so an unstyled <a> renders as plain,
// non-underlined paragraph text and is invisible as a link. Previously,
// the <Disclaimer> wrapper (ui/Disclaimer.tsx) injected underline styling
// by walking its children and cloning any raw <a> element it found. That
// worked when disclaimer copy was a flat JSX fragment of literal <a> tags,
// but it can't see inside a <Trans> component's rendered output, so once
// disclaimer copy moved to <Trans> for bilingual support, every link
// silently lost its styling. Links must now be styled explicitly at the
// <Trans> `components` call site instead of relying on that reflection.
export const DISCLAIMER_LINK_CLASS_NAME = 'text-inherit underline hover:text-gray-600 transition-colors';

/**
 * Shared disclaimer content used across form steps.
 *
 * This must stay a component (not a module-level JSX constant) so that each
 * usage site creates a fresh `<Trans>` element on every render. A cached
 * constant would give `<Trans>` the exact same props object on every render,
 * which lets React's reconciler bail out of re-rendering it entirely when an
 * ancestor re-renders for an unrelated reason (e.g. a language switch),
 * leaving the disclaimer stuck in whatever language it first rendered in
 * until a full page reload remounts it from scratch.
 */
export default function DisclaimerContent() {
  return (
    <Trans
      i18nKey="disclaimer.consent"
      ns="common"
      components={{
        termsLink: (
          <a
            href="https://symplelending.com/terms-of-use"
            className={DISCLAIMER_LINK_CLASS_NAME}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
        esignLink: (
          <a
            href="https://symplelending.com/esign-consent"
            className={DISCLAIMER_LINK_CLASS_NAME}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
        arbitrationLink: (
          <a
            href="https://symplelending.com/arbitration-agreement"
            className={DISCLAIMER_LINK_CLASS_NAME}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
        privacyLink: (
          <a
            href="https://symplelending.com/privacy-policy"
            className={DISCLAIMER_LINK_CLASS_NAME}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
      }}
    />
  );
}
